import Vue from 'vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

Vue.component('load-filter-component', {
    props: {
        loadMoreAmount: {
            type: Number,
            default: 4,
        },

        items: {
            type: Array,
        },

        defaultCategory: {
            type: String,
            default: null,
        },

        infinityScroll: {
            type: Boolean,
            default: false,
        },
    },
    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            currentIndex: 1,
            maxIndex: 1,
            noMoreItems: false,
            enabledCategories: ['filter-all'],
            categoryTitle: null,
            pillFilter: [],
        };
    },

    computed: {
        multiMatchFormattedData() {
            let filteredItems = this.items.filter((item) => {
                // If all return all
                if (this.enabledCategories.indexOf('all-pill') > -1 && this.enabledCategories.indexOf('filter-all') > -1) {
                    return item;
                }

                const matches = this.enabledCategories.every((value) => {
                    if (item.categories.includes(value)) {
                        return item;
                    }

                    return false;
                });

                return matches;
            });

            if (this.enabledCategories.length === 0) {
                filteredItems = this.items;
            }

            const formattedData = filteredItems.slice(0, (this.currentIndex * this.loadMoreAmount));
            return formattedData;
        },

        singleMatchFormattedData() {
            let filteredItems = this.items.filter((item) => {
                for (let i = 0; i < this.enabledCategories.length; i++) {
                    if (item.categories.indexOf(this.enabledCategories[i]) !== -1 || this.enabledCategories[i] === 'filter-all') {
                        return item;
                    }
                }

                return false;
            });

            if (this.enabledCategories.length === 0) {
                filteredItems = this.items;
            }

            this.$nextTick(() => {
                this.revealCards();
            });

            const formattedData = filteredItems.slice(0, (this.currentIndex * this.loadMoreAmount));
            return formattedData;
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        this.currentIndex = 1;
        this.maxIndex = Math.ceil(this.items.length / this.loadMoreAmount);

        if (this.items.length <= this.loadMoreAmount) {
            this.noMoreItems = true;
        }

        if (this.defaultCategory) {
            this.enabledCategories = [this.defaultCategory];
        }

        if (this.infinityScroll) {
            this.initScrollLoadMore();
        }

        this.preLoadImages();
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        filterOptionChange(category) {
            this.setCategories(category.value, null, category.title, false);
        },

        categoriesChanged(categories) {
            this.enabledCategories = categories;
        },

        /**
         * Infinity scroll will be enabled when the prop "infinityScroll" is set to true
         * This requires the use of a ref "itemContainer" which tells the scroll trigger
         * where it should look for when checking if the next items should be loaded.
         *
         * @return void
         */
        initScrollLoadMore() {
            const container = this.$refs.itemContainer;

            if (!container) {
                return;
            }

            ScrollTrigger.create({
                trigger: container,
                start: 'top 80%',
                end: 'bottom bottom-=10%',
                // markers: { startColor: 'red', endColor: 'red' },
                onLeave: (instance) => {
                    this.loadNextItems(instance);
                },
            });
        },

        /**
         * This function will trigger the next set of items to show
         *
         * @param {ScrollTrigger} instance - mandatory
         * @returns void
         */
        loadNextItems(instance = null) {
            if (this.currentIndex === this.maxIndex || this.currentIndex > this.maxIndex) {
                return;
            }

            this.currentIndex += 1;
            this.noMoreItems = this.currentIndex === this.maxIndex || this.currentIndex > this.maxIndex;

            this.preLoadImages();

            if (!instance || !this.infinityScroll) {
                return;
            }

            // Nexttick is needed to refresh the ScrollTrigger instance and update the end position of the trigger after the layout has changed
            this.$nextTick(() => {
                instance.refresh();
            });
        },

        revealCards() {
            const elements = [
                ...document.querySelectorAll('[data-reveal]'),
                ...document.querySelectorAll('[data-fade-reveal]'),
                ...document.querySelectorAll('[data-image-reveal]'),
            ];

            elements.forEach((element) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === '') {
                    return;
                }

                const startPercentage = revealPosition || '90%';

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        if (!revealDelay) {
                            element.classList.add('--revealed');
                            return;
                        }

                        gsap.delayedCall(revealDelay, () => {
                            element.classList.add('--revealed');
                        });
                    },
                });
            });
        },

        setCategories(categoryId, filter, categoryTitle = null, multiSelect = false) {
            if (!categoryId || this.enabledCategories.indexOf(categoryId) !== -1) {
                return;
            }

            if (categoryTitle) {
                this.categoryTitle = categoryTitle;
            }

            this.enabledCategories = multiSelect ? [...this.enabledCategories, categoryId] : [categoryId];
            this.toggleFilter(filter);
        },

        removeFilter(categoryId, filter) {
            if (!categoryId) {
                return;
            }

            const index = this.enabledCategories.indexOf(categoryId);
            if (index === -1) {
                return;
            }

            this.enabledCategories.splice(index, 1);
            this.toggleFilter(filter);
        },

        selectFilterOption(categoryId) {
            if (!categoryId) {
                return;
            }

            const index = this.enabledCategories.indexOf(categoryId);

            if (index > -1) {
                this.enabledCategories = ['filter-all'];
                return;
            }

            this.enabledCategories = [categoryId];
        },

        toggleFilter(filter) {
            const target = this.$refs[filter];

            if (!target) {
                return;
            }

            const filterBlock = target;
            const filterList = target.querySelector('[data-list]');
            const filterArrow = target.querySelector('[data-arrow-holder] svg');
            const tl = gsap.timeline();

            if (filterBlock.classList.contains('active')) {
                filterBlock.classList.remove('active');
                tl
                    .to(filterList, {
                        duration: 0.3,
                        height: 0,
                        ease: 'power4.out',
                    })
                    .to(filterArrow, {
                        duration: 0.3,
                        rotate: '0deg',
                        ease: 'power4.out',
                    }, '<');
                return;
            }

            filterBlock.classList.add('active');
            tl
                .to(filterList, {
                    duration: 0.3,
                    height: 'auto',
                    ease: 'power4.out',
                })
                .to(filterArrow, {
                    duration: 0.3,
                    rotate: '180deg',
                    ease: 'power4.out',
                }, '<');
        },

        preLoadImages() {
            if (this.noMoreItems) {
                return;
            }

            const nextIndex = this.currentIndex + 1;
            const nextItems = this.items.slice((this.currentIndex * this.loadMoreAmount), (nextIndex * this.loadMoreAmount));

            for (let i = 0; i < nextItems.length; i++) {
                if (nextItems[i].image) {
                    let { image } = nextItems[i];

                    if (nextItems[i].imageToLoad) {
                        image = nextItems[i].imageToLoad;
                    }

                    const img = new Image();
                    img.src = image;
                }
            }
        },
    },
});
